import React, { useMemo } from 'react';
import { SlideOutComp, SlideOutCompProps } from '@moderepo/mode-app-components';
import { SlideOutPanelOptions } from '@moderepo/mode-ui-state';
import { makeStyles } from '@material-ui/core';
import { ProjectTranslationContainer } from './ProjectTranslationContainer';
import { ProjectTypeHandlersManager, ProjectTypeSlideOutCompHandler } from '../../utils/ritzExtensionUtils';

/**
 * These are the list of components that can be displayed in the SlideOut panel.
 */
export enum ProjectTranslationSlideOutPanelCompType {
    PROJECT_TRANSLATION_PREVIEW = 'project_translation_preview',
}

/**
 * The options required for showing preview of "Generate Project Translation JSON" in the Slide Out panel
 */
export interface ProjectTranslationPreviewCompOptions extends SlideOutPanelOptions {
    readonly compType: ProjectTranslationSlideOutPanelCompType.PROJECT_TRANSLATION_PREVIEW;
    readonly compProps: {
        readonly data: {
            readonly translationJSON: string;
        }
    }
}

/**
 * Type guard check to make sure the provided options is the options for ProjectJsonPreviewCompOptions
 */
export const isProjectTranslationPreviewCompOptions = (options: unknown): options is ProjectTranslationPreviewCompOptions => {
    return (options as SlideOutPanelOptions)?.compType === ProjectTranslationSlideOutPanelCompType.PROJECT_TRANSLATION_PREVIEW;
};

/**
 * When we register the slide out panels with the ProjectTypeHandlersManager, we need to give it a ProjectTypeSlideOutCompHandler. The
 * ProjectTypeHandlersManager doesn't know how to create slide out panels therefore it depends on the ProjectTypeSlideOutCompHandler. Whenever
 * the ProjectTypeHandlersManager needs to create slide out panel for a given type, it will call the handler to create it. This is the
 * implementation of the ProjectTypeSlideOutCompHandler interface which has only 1 function, "getSlideOutComponent".
 */
const ProjectTranslationSlideOutComponentsHandler: ProjectTypeSlideOutCompHandler = {

    getSlideOutComponent: (options: SlideOutPanelOptions): React.ReactElement<SlideOutCompProps> | undefined => {
        if (isProjectTranslationPreviewCompOptions(options)) {
            return (
                <ProjectTranslationPreviewContainer
                    translationJSON={options.compProps.data.translationJSON}
                />
            );
        }
        return undefined;
    },
};

/**
 * Register the slide out panel types and the handler that handle those types.
 */
ProjectTypeHandlersManager.getInstance().registerSlideOutComponentHandler(
    ProjectTranslationSlideOutPanelCompType.PROJECT_TRANSLATION_PREVIEW, ProjectTranslationSlideOutComponentsHandler,
);

const useStyle = makeStyles(() => {
    return {
        root: {
            '& .slide-out-comp-container': {
                minWidth: 1000,
            },
        },

        projectTranslationContainer: {
            height: '100%',
            width : '100%',
        },
    };
});


interface TranslationJsonPreviewContainerProps {
    readonly translationJSON: string;
}

export const ProjectTranslationPreviewContainer = (props: TranslationJsonPreviewContainerProps) => {
    const { translationJSON } = props;
    const classes = useStyle();

    const slideOutCompContent = useMemo(() => {
        return (
            <ProjectTranslationContainer
                translationJSON={translationJSON}
                className={classes.projectTranslationContainer}
            />
        );
    }, [classes.projectTranslationContainer, translationJSON]);

    return (
        <SlideOutComp
            className={classes.root}
            compContent={slideOutCompContent}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
